import { v4 as uuid } from 'uuid';
import ROUTES from '../../constants/routes';
import { hasRepPermissions } from '../../utils/permissions';
import { getSupportGroupsAccessDecision, SUPPORT_GROUP_SUBSCRIPTION_STATUSES } from '../../utils/supportGroupsAccessUtils';
import { canAccessProgressLog } from '../../utils/progressLogAccessUtils';
import { isListNotEmpty, isBooleanTrue } from '../../utils/objectUtils';

/**
 * Builds the V2 account sidebar menu based on subscription state, permissions,
 * and support group access.
 *
 * @param {object} [options]
 * @param {object} [options.authContext] - Current authorization context used for permission checks.
 * @param {Array<object>} [options.subscriptions=[]] - User subscriptions used to gate account sections.
 * @param {Array<object>} [options.programs=[]] - Program records used to evaluate support group access.
 * @returns {Array<object>} Sidebar menu items consumed by the V2 vertical navigation.
 */
const AccountMenu = ({ authContext, subscriptions = [], programs = [] } = {}) => {
	const hasAnySubscription = isListNotEmpty(subscriptions);
	const hasActiveSubscription = subscriptions.some((sub) => SUPPORT_GROUP_SUBSCRIPTION_STATUSES.includes(sub?.status));
	const supportGroupsAccessDecision = getSupportGroupsAccessDecision({ authContext, subscriptions, programs });
	const hasSupportGroupsAccess = supportGroupsAccessDecision.canAccess;
	const hasProgressLogAccess = canAccessProgressLog({ authContext, subscriptions, programs });
	const isRep = hasRepPermissions(authContext);


	return [
		{
			id: uuid(),
			title: 'Home',
			link: ROUTES.ACCOUNT,
		},
		{
			id: uuid(),
			title: 'My Treatments',
			link: ROUTES.MY_TREATMENTS,
		},
		{
			id: uuid(),
			title: 'Messages',
			link: ROUTES.MY_MESSAGES,
			show: isBooleanTrue(hasAnySubscription) || isBooleanTrue(isRep),
		},
		{
			id: uuid(),
			title: 'Progress log',
			link: ROUTES.MY_CHECK_INS,
			show: hasProgressLogAccess,
		},
		{
			id: uuid(),
			title: 'Meal plans',
			link: ROUTES.MY_MEAL_PLANS,
			show: isBooleanTrue(hasActiveSubscription) || isBooleanTrue(isRep),
		},
		{
			id: uuid(),
			title: 'Support groups',
			link: ROUTES.MY_EVENTS,
			show: hasSupportGroupsAccess,
		},
		{
			id: uuid(),
			title: 'Referral program',
			link: ROUTES.MY_REFERRALS,
		},
		{
			id: uuid(),
			title: 'Store',
			link: ROUTES.MY_STORE,
		},
		{
			id: uuid(),
			title: 'Support',
			link: ROUTES.MY_SUPPORT_TICKETS,
		},
		{
			id: uuid(),
			title: 'Account management',
			children: [
				{
					id: uuid(),
					title: 'Multi-Factor Auth',
					link: ROUTES.MY_MFA,
				},
				{
					id: uuid(),
					title: 'Change password',
					link: ROUTES.PASSWORD_CHANGE,
				},
				{
					id: uuid(),
					title: 'Change phone number',
					link: ROUTES.CHANGE_PHONE,
				},
				{
					id: uuid(),
					title: 'Contact us',
					link: ROUTES.MY_CONTACT,
				},
			],
		},
	];
};

export default AccountMenu;
